import Router, { useRouter } from 'next/router';
import { useCallback, useMemo } from 'react';

import { type ShortUrlResponse } from '@air/api/types';

import { findShowcaseSubPageForPath } from '~/components/v2/components/showcase/showcaseSubPageBoardContext';
import {
  type PublicShowcaseContextValue,
  type PublicShowcaseLayoutActivePage,
} from '~/providers/PublicShowcaseProvider';

export type UsePublicUrlShowcasePageNavigationProps = {
  /** The surface's route base — the live viewer's /a/{shortId} or the preview's own base. */
  basePath: string;
  /** The link's sub-pages; the current page is resolved from the route against these. */
  presentationPages?: ShortUrlResponse['presentationPages'];
};

/**
 * The URL surfaces' page-navigation implementation: the page is read off the route (a
 * `/page/{slug}` segment, or a board route's `?page={slug}` trail), selection is a shallow route
 * push, and page entries get real hrefs. Both the live /a page and the Preview iframe (which
 * carries the same viewer segments on its own base path) supply this to PublicShowcaseLayout's
 * contract, and feed `activePage` into usePublicUrlBoardNavigation for board-drill trails.
 */
export const usePublicUrlShowcasePageNavigation = ({
  basePath,
  presentationPages,
}: UsePublicUrlShowcasePageNavigationProps) => {
  const { asPath } = useRouter();

  // The page the viewer is on, read off the route — a `/page/{slug}` segment, or a board route's
  // `?page={slug}` trail. Null on Home, on links with no sub-pages, and on the board/clip mounts
  // (no presentationPages).
  const activePage = useMemo<PublicShowcaseLayoutActivePage>(() => {
    const subPages = presentationPages?.subPages;

    if (!subPages?.length) {
      return null;
    }

    const currentSubPage = findShowcaseSubPageForPath({ path: asPath, subPages });

    return currentSubPage ? { pageId: currentSubPage.subPage.collectionId, pageSlug: currentSubPage.slug } : null;
  }, [asPath, presentationPages]);

  const getPageNavigationHref = useCallback<NonNullable<PublicShowcaseContextValue['getPageNavigationHref']>>(
    ({ pageSlug }) => (pageSlug ? `${basePath}/page/${encodeURIComponent(pageSlug)}` : basePath),
    [basePath],
  );

  const onSelectPage = useCallback<PublicShowcaseContextValue['onSelectPage']>(
    (page) => {
      void Router.push(getPageNavigationHref({ pageSlug: page?.pageSlug ?? null }), undefined, { shallow: true });
    },
    [getPageNavigationHref],
  );

  return { activePage, getPageNavigationHref, onSelectPage };
};
